/**
 * Danger zone component for admin workspace detail page.
 * Contains destructive actions like block, unblock, soft delete, restore.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAdminBlockWorkspace,
  useAdminUnblockWorkspace,
  useAdminSoftDeleteWorkspace,
  useAdminRestoreWorkspace,
  useAdminUpdateWorkspace,
} from "../../hooks/useAdminWorkspaces";
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  Trash2,
  RotateCcw,
  Loader2,
  AlertCircle,
  AtSign,
} from "lucide-react";
import type { AdminWorkspaceDetail } from "../../services/naraDoor";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface DangerZoneProps {
  workspace: AdminWorkspaceDetail;
}

export function DangerZone({ workspace }: DangerZoneProps) {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showHandleDialog, setShowHandleDialog] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [newHandle, setNewHandle] = useState("");
  const [handleConfirmation, setHandleConfirmation] = useState("");

  const blockMutation = useAdminBlockWorkspace();
  const unblockMutation = useAdminUnblockWorkspace();
  const deleteMutation = useAdminSoftDeleteWorkspace();
  const restoreMutation = useAdminRestoreWorkspace();
  const updateMutation = useAdminUpdateWorkspace();
  const devMutation = useAdminUpdateWorkspace();

  const isBlocked = workspace.is_blocked;
  const isDeleted = !!workspace.deleted_at;

  const handleBlock = async () => {
    try {
      await blockMutation.mutateAsync({
        workspaceId: workspace.id,
        reason: blockReason.trim() || undefined,
      });
      setShowBlockDialog(false);
      setBlockReason("");
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleUnblock = async () => {
    try {
      await unblockMutation.mutateAsync({ workspaceId: workspace.id });
      setShowUnblockDialog(false);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ workspaceId: workspace.id });
      setShowDeleteDialog(false);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleRestore = async () => {
    try {
      await restoreMutation.mutateAsync({ workspaceId: workspace.id });
      setShowRestoreDialog(false);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleUpdateHandle = async () => {
    const trimmedHandle = newHandle.trim().toLowerCase();
    if (!trimmedHandle) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        workspaceId: workspace.id,
        handle: trimmedHandle,
      });
      setShowHandleDialog(false);
      setNewHandle("");
      setHandleConfirmation("");
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleToggleDev = (isDev: boolean) => {
    devMutation.mutate(
      { workspaceId: workspace.id, is_dev: isDev },
      { onError: (err) => console.error("DangerZone: dev flag update failed", workspace.id, err) }
    );
  };

  const isValidHandle = (handle: string) => {
    return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(handle);
  };

  const canSubmitHandle =
    newHandle.trim().length > 0 &&
    isValidHandle(newHandle.trim().toLowerCase()) &&
    handleConfirmation === workspace.handle;

  return (
    <div className="space-y-6">
      {/* Status Display */}
      {(isBlocked || isDeleted) && (
        <div className="space-y-3">
          {isDeleted && (
            <div className="flex items-center gap-3 p-4 bg-muted border border-border rounded-lg">
              <Trash2 className="h-5 w-5 text-muted-foreground shrink-0" />
              <div>
                <p className="font-medium text-foreground">Workspace is soft-deleted</p>
                {workspace.deleted_at && (
                  <p className="text-sm text-muted-foreground">
                    Deleted on {formatDate(workspace.deleted_at, "date")}
                  </p>
                )}
              </div>
            </div>
          )}
          {isBlocked && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
              <Ban className="h-5 w-5 text-red-600 shrink-0" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">Workspace is blocked</p>
                {workspace.blocked_reason && (
                  <p className="text-sm text-red-600">
                    Reason: {workspace.blocked_reason}
                  </p>
                )}
                {workspace.blocked_at && (
                  <p className="text-sm text-red-600">
                    Blocked on {formatDate(workspace.blocked_at, "date")}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Danger Zone Section */}
      <div className="border-2 border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Danger Zone</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          These actions can significantly affect the workspace and its users.
          Please proceed with caution.
        </p>

        <div className="space-y-4">
          {/* Change Handle */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium text-foreground">Change Workspace Handle</p>
              <p className="text-sm text-muted-foreground">
                Current handle: <span className="font-mono">@{workspace.handle || "none"}</span>.
                Changing the handle will break existing links.
              </p>
            </div>
            <button
              onClick={() => {
                setNewHandle(workspace.handle || "");
                setHandleConfirmation("");
                setShowHandleDialog(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <AtSign className="h-4 w-4" />
              Change Handle
            </button>
          </div>

          {/* Block / Unblock */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium text-foreground">
                {isBlocked ? "Unblock Workspace" : "Block Workspace"}
              </p>
              <p className="text-sm text-muted-foreground">
                {isBlocked
                  ? "Allow users to access this workspace again."
                  : "Prevent all users from accessing this workspace."}
              </p>
            </div>
            {isBlocked ? (
              <button
                onClick={() => setShowUnblockDialog(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="h-4 w-4" />
                Unblock
              </button>
            ) : (
              <button
                onClick={() => setShowBlockDialog(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Ban className="h-4 w-4" />
                Block
              </button>
            )}
          </div>

          {/* Dev workspace: changes what Delete does, so it sits above it */}
          <div className="flex items-start justify-between gap-4 p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium text-foreground">{t("adminWorkspaces.detail.devWorkspace.label")}</p>
              <p className="text-sm text-muted-foreground">{t("adminWorkspaces.detail.devWorkspace.help")}</p>
              {devMutation.error && (
                <p className="mt-1 text-sm text-red-600">{t("adminWorkspaces.detail.devWorkspace.error")}</p>
              )}
            </div>
            <input
              type="checkbox"
              aria-label={t("adminWorkspaces.detail.devWorkspace.label")}
              checked={workspace.is_dev ?? false}
              disabled={devMutation.isPending}
              onChange={(e) => handleToggleDev(e.target.checked)}
              className="h-5 w-5 mt-0.5 shrink-0 accent-orange-600"
            />
          </div>

          {/* Delete / Restore */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium text-foreground">
                {isDeleted ? "Restore Workspace" : "Soft Delete Workspace"}
              </p>
              <p className="text-sm text-muted-foreground">
                {isDeleted
                  ? "Restore this workspace and make it visible again."
                  : "Mark this workspace as deleted. It can be restored later."}
              </p>
            </div>
            {isDeleted ? (
              <button
                onClick={() => setShowRestoreDialog(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Restore
              </button>
            ) : (
              <button
                onClick={() => setShowDeleteDialog(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Block Dialog */}
      {showBlockDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <Ban className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Block Workspace</h2>
            </div>
            <p className="text-muted-foreground mb-4">
              This will prevent all users from accessing{" "}
              <strong>{workspace.name}</strong>. The workspace can be unblocked later.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-1">
                Reason (optional)
              </label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Enter reason for blocking..."
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none h-20"
              />
            </div>

            {blockMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{blockMutation.error.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBlockDialog(false);
                  setBlockReason("");
                }}
                disabled={blockMutation.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBlock}
                disabled={blockMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {blockMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Block Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unblock Dialog */}
      {showUnblockDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Unblock Workspace</h2>
            </div>
            <p className="text-muted-foreground mb-6">
              This will allow users to access <strong>{workspace.name}</strong> again.
            </p>

            {unblockMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{unblockMutation.error.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowUnblockDialog(false)}
                disabled={unblockMutation.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUnblock}
                disabled={unblockMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {unblockMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Unblock
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Delete Workspace</h2>
            </div>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to soft delete <strong>{workspace.name}</strong>?
              The workspace can be restored later if needed.
            </p>

            {deleteMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{deleteMutation.error.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Dialog */}
      {showRestoreDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <RotateCcw className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Restore Workspace</h2>
            </div>
            <p className="text-muted-foreground mb-6">
              This will restore <strong>{workspace.name}</strong> and make it visible to
              users again.
            </p>

            {restoreMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{restoreMutation.error.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRestoreDialog(false)}
                disabled={restoreMutation.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                disabled={restoreMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {restoreMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Restore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Handle Dialog */}
      {showHandleDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <AtSign className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Change Workspace Handle</h2>
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                Changing the handle will break all existing links to this workspace.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  New Handle
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-muted border border-r-0 border-border rounded-l-lg text-muted-foreground">
                    @
                  </span>
                  <input
                    type="text"
                    value={newHandle}
                    onChange={(e) => setNewHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    placeholder="new-handle"
                    className="flex-1 px-3 py-2 border border-border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Only lowercase letters, numbers, and hyphens. Must start and end with a letter or number.
                </p>
                {newHandle && !isValidHandle(newHandle.trim().toLowerCase()) && (
                  <p className="mt-1 text-xs text-red-600">
                    Invalid handle format.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  To confirm, type the current handle: <span className="font-mono text-orange-600">{workspace.handle}</span>
                </label>
                <input
                  type="text"
                  value={handleConfirmation}
                  onChange={(e) => setHandleConfirmation(e.target.value)}
                  placeholder={workspace.handle || ""}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            {updateMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{updateMutation.error.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowHandleDialog(false);
                  setNewHandle("");
                  setHandleConfirmation("");
                }}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateHandle}
                disabled={!canSubmitHandle || updateMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Change Handle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
